import { createFileRoute, getRouteApi } from "@tanstack/react-router";

import { NavBar } from "@/components/client-navigation-bar";
import { ExplorePage } from "@/components/explore/explore-page";
import { fetchNavPages } from "@/lib/page-data";
import { episodesInfiniteQuery } from "@/lib/queries";
import { queryClient } from "@/lib/query-client";

const routeApi = getRouteApi("/explore");

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

const dateParam = (value: unknown): string | undefined =>
  typeof value === "string" && DATE_PATTERN.test(value) ? value : undefined;

const ExploreRoute = () => {
  const { pages } = routeApi.useLoaderData();
  const { from, to } = routeApi.useSearch();

  return (
    <div className="min-h-[100dvh]">
      <NavBar pages={pages} />
      <ExplorePage initialFromDate={from} initialToDate={to} />
    </div>
  );
};

export const Route = createFileRoute("/explore")({
  component: ExploreRoute,

  /**
   * Fetching during navigation rather than in an effect after hydration.
   *
   * The page used to render an empty grid, wait for the bundle to download,
   * parse and hydrate, and only then start requesting episodes — so nothing
   * appeared for well over a second. Loading here means the request runs on
   * the server and the first page ships inside the HTML.
   */
  loader: async () => {
    const [pages] = await Promise.all([
      fetchNavPages(),
      queryClient.ensureInfiniteQueryData(episodesInfiniteQuery()),
    ]);
    return { pages };
  },

  validateSearch: (
    search: Record<string, unknown>
  ): { from?: string; to?: string } => {
    const result: { from?: string; to?: string } = {};
    const from = dateParam(search.from);
    const to = dateParam(search.to);
    if (from) {
      result.from = from;
    }
    if (to) {
      result.to = to;
    }
    return result;
  },
});
